import { useQuery } from '@tanstack/react-query';

import config from '@utils/config';
import {
    getAccessTokenOptions,
    getFrontTokenOptions,
    refreshTokenOptions,
} from '@utils/cookieConfig';

import { useCookies } from 'react-cookie';
import { doRefreshToken } from '@apis/utils';

enum SessionAPIEndpoints {
    'sessionInfo' = 'session/info',
    'refreshToken' = 'session/refresh',
}

const API_ENDPOINT = config.naraBackendURI;

const getCookiesPath = (isTemporarySession: boolean) => {
    if (isTemporarySession) {
        return {
            accessToken: 'tempAccessToken',
            frontToken: 'tempFrontToken',
            refreshToken: 'tempRefreshToken',
        };
    }
    return {
        accessToken: 'accessToken',
        frontToken: 'frontToken',
        refreshToken: 'refreshToken',
    };
};

export const getSessionInfo = async ({
    accessToken,
    accessTokenPath,
    setCookie,
}: {
    accessToken?: string;
    accessTokenPath: string;
    setCookie?: any;
}) => {
    if (!accessToken) {
        throw new Error('Access Token is Not Provided');
    }
    const response = await fetch(
        `${API_ENDPOINT}/${SessionAPIEndpoints.sessionInfo}`,
        {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${accessToken}`,
            },
        }
    );
    const data = await response.json();
    if (data.message === 'try refresh token') {
        const cookiePaths = getCookiesPath(accessTokenPath === 'tempAccessToken');
        // Use the shared refresh mutex to avoid concurrent refresh requests
        // that would trigger SuperTokens "token theft detected".
        const newTokens = await doRefreshToken(cookiePaths.refreshToken);

        // Set the new tokens in the cookies
        setCookie(
            cookiePaths.accessToken,
            newTokens.accessToken,
            getAccessTokenOptions(newTokens.accessToken)
        );
        setCookie(
            cookiePaths.frontToken,
            newTokens.frontToken,
            getFrontTokenOptions(newTokens.accessToken)
        );
        if (newTokens.refreshToken) {
            setCookie(
                cookiePaths.refreshToken,
                newTokens.refreshToken,
                refreshTokenOptions
            );
        }
        // Retry the request with the new token
        const retryResponse = await fetch(
            `${API_ENDPOINT}/${SessionAPIEndpoints.sessionInfo}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${newTokens.accessToken}`,
                },
            }
        );

        return retryResponse.json();
    }

    if (!response.ok) {
        throw new Error(`Session info failed: ${response.status}`);
    }

    return data;
};

export function useGetSessionInfo({
    accessToken,
    accessTokenPath = 'accessToken',
}: {
    accessToken?: string;
    accessTokenPath?: string;
}) {
    const [cookies, setCookie] = useCookies([
        'accessToken',
        'frontToken',
        'refreshToken',
    ]);

    return useQuery({
        queryKey: ['getSessionInfo'],
        enabled: !!accessToken,
        retry: false,
        staleTime: 60_000,
        refetchOnWindowFocus: false,
        queryFn: () =>
            getSessionInfo({
                accessToken,
                accessTokenPath,
                setCookie,
            }),
    });
}

export const logoutSession = async (
    accessToken: string,
    email: string
): Promise<void> => {
    await fetch(`${API_ENDPOINT}/sessions/all`, {
        method: 'DELETE',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
    });
};
